import { CardContent } from '@/components/ui';
import { centsToPayout } from '@/lib/binary-market';
import { sportsBuyOption, sportsColor } from '@/lib/sports-market';
import type { OrderSide } from '@/analytics';
import type { EventCardDto, MarketOptionDto, SportsMoneylineDto } from '@/lib/models';
import { useEventLiveStatus } from '@/lib/realtime';
import { LiveBadge } from '../BinaryMarkets/LiveBadge';
import { LobbyEventCard } from '../BinaryMarkets/LobbyEventCard';
import { MarketOutcomePricesSubscriber, useOutcomePrices } from '../BinaryMarkets/useOutcomePrices';
import { useCategoryMeta } from '../CategoryTabs/useCategoryMeta';
import { SportsMoneylineRow } from './SportsMoneylineRow';

export interface SportsMoneylineCardProps {
  /** A lobby event card whose `sportsMoneyline` block is present (a MATCH event). */
  event: EventCardDto;
  /** The event's moneyline block (2-way or 3-way). */
  moneyline: SportsMoneylineDto;
  /** Opens the shared buy-in popup pre-set to the tapped answer (ARC-80 §4.3/§7). */
  onOpenBuy: (option: MarketOptionDto, side: OrderSide) => void;
}

/** `Dec 31 @ 3:00 PM`, or undefined when absent/unparseable. */
function formatEventDate(iso: string | null): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} @ ${time}`;
}

/**
 * Lobby card for a SPORTS match event (ARC-80 §4): the league eyebrow + matchup,
 * then one {@link SportsMoneylineRow} per moneyline answer (2-way, or 3-way incl.
 * Draw). Tapping the card opens the event page; tapping a row's `% Yes` pill opens
 * the buy-in popup. Live prices overlay the seed via Centrifugo.
 */
export function SportsMoneylineCard({ event, moneyline, onOpenBuy }: SportsMoneylineCardProps) {
  const { prices, setPrice } = useOutcomePrices();
  const marketLabel = event.marketCount === 1 ? '1 Market' : `${event.marketCount} Markets`;
  const categoryMeta = useCategoryMeta(event.categoryCode);
  const categoryName = categoryMeta?.displayName ?? 'Sports';
  const eyebrow = event.subCategoryName ? `${categoryName} – ${event.subCategoryName}` : categoryName;
  // Live (in-play) events show the live badge in place of the kick-off date —
  // seeded from REST, flipped in-session over the per-event realtime channel.
  const isLive = useEventLiveStatus(event.id, event.eventKey ?? null, event.isLive);
  const subtitle = isLive ? <LiveBadge startTime={event.startTime} /> : formatEventDate(event.startTime);

  return (
    <LobbyEventCard
      eventId={event.id}
      name={event.name}
      eyebrow={eyebrow}
      eyebrowIconUrl={categoryMeta?.iconImgUrl}
      subtitle={subtitle}
      closeTime={event.closeTime}
      volume={event.volume}
      marketLabel={marketLabel}
    >
      <MarketOutcomePricesSubscriber
        marketId={moneyline.marketId}
        externalMarketId={moneyline.externalMarketId}
        onPrice={setPrice}
      />

      <CardContent className="flex-1 gap-0 px-5">
        {moneyline.outcomes.map((o) => {
          const percent = prices[o.externalOutcomeId]?.yesCents ?? o.chancePercent;
          return (
            <SportsMoneylineRow
              key={o.outcomeId}
              label={o.label}
              shortName={o.shortName}
              imageUrl={o.imageUrl}
              color={sportsColor(o.color, o.side)}
              percent={percent}
              payout={centsToPayout(percent)}
              onSelect={() => onOpenBuy(sportsBuyOption(o, moneyline), 'yes')}
            />
          );
        })}
      </CardContent>
    </LobbyEventCard>
  );
}
